import constate from 'constate';
import { useCallback, useRef, useState } from 'react';
import { z } from 'zod';

import { BrowserStorageVariant, useBrowserStorage } from 'common';

export enum SideNavigationMode {
  OverlayOnHover = 'OverlayOnHover',
  Fixed = 'Fixed',
}

export enum SideNavigationState {
  Expanded = 'Expanded',
  Collapsed = 'Collapsed',
}

function useSideNavigationMode() {
  const { storedValues, updateStoredValue } = useBrowserStorage({
    storageKeyName: 'SideNavigationMode',
    variant: BrowserStorageVariant.LocalStorage,
    schema: z.object({
      sideNavigationMode: z.enum(SideNavigationMode),
    }),
    initialValue: {
      sideNavigationMode: SideNavigationMode.Fixed,
    },
  });

  const [sideNavigationMode, setSideNavigationMode] = useState(storedValues.sideNavigationMode);
  const [isNavigationHovering, _setIsNavigationHovering] = useState(false);
  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);

  const hoverLockUntilRef = useRef<number>(0);

  const setIsNavigationHoveringIfAfterDelay = useCallback((value: boolean) => {
    if (Date.now() < hoverLockUntilRef.current) {
      return;
    }
    _setIsNavigationHovering(value);
  }, []);

  const sideNavigationState =
    sideNavigationMode === SideNavigationMode.Fixed || isNavigationHovering || isSearchInputFocused
      ? SideNavigationState.Expanded
      : SideNavigationState.Collapsed;

  return {
    sideNavigationMode,
    sideNavigationState,
    setSideNavigationModeToFixed: () => {
      setSideNavigationMode(SideNavigationMode.Fixed);
      updateStoredValue('sideNavigationMode', SideNavigationMode.Fixed);
    },
    setSideNavigationModeToOverlayOnHover: () => {
      setSideNavigationMode(SideNavigationMode.OverlayOnHover);
      updateStoredValue('sideNavigationMode', SideNavigationMode.OverlayOnHover);
      _setIsNavigationHovering(false);
      // Since toggle button is inside sidenav,
      // This is needed to make sure drawer get collapsed after a toggle to OverlayOnHover mode
      hoverLockUntilRef.current = Date.now() + 200;
    },
    isNavigationHovering,
    setIsNavigationHoveringIfAfterDelay,
    isSearchInputFocused,
    setIsSearchInputFocused,
  };
}

export const [SideNavigationModeProvider, useSideNavigationModeContext] =
  constate(useSideNavigationMode);
